// ---------------------------------------------------------------------------
// auth error handling utilities
// ---------------------------------------------------------------------------

export interface AuthErrorResult {
  message: string;
  code: string | null;
}

// ---------------------------------------------------------------------------
// error code mappings
// ---------------------------------------------------------------------------

const AUTH_ERROR_MESSAGES: Record<string, string> = {
  InvalidSecret:
    "Invalid email or password. Please check your credentials and try again.",
  InvalidPassword:
    "Invalid password. Please check your password and try again.",
  InvalidEmail: "Invalid email address. Please check your email and try again.",
  InvalidAccountId:
    "No staff account matches that email. Use the address your admin set up, or ask them to invite you.",
  AccountNotFound: "No account found with this email address.",
  AccountAlreadyExists: "An account with this email already exists.",
  AuthenticationTimeout: "Authentication timed out. Please try again.",
  NotAuthenticated: "You are not authenticated. Please sign in.",
  INVALID_PASSWORD: "Password does not meet requirements.",
  ACCOUNT_CREATION_DISABLED: "Account creation is currently disabled.",
  SignInIncomplete:
    "Sign-in did not finish. Check your email and password, or ask your admin to confirm your account is active.",
  "Invalid credentials":
    "Invalid email or password. Please check your credentials and try again.",
};

// ---------------------------------------------------------------------------
// extract error code from error
// ---------------------------------------------------------------------------

function convexStyleErrorData(error: unknown): string {
  if (typeof error !== "object" || error === null) {
    return "";
  }
  const record = error as Record<string, unknown>;
  const data = record["data"];
  if (typeof data === "string") {
    return data;
  }
  if (typeof data === "object" && data !== null) {
    const inner = data as Record<string, unknown>;
    const code = inner["code"];
    if (typeof code === "string") {
      return code;
    }
    try {
      return JSON.stringify(data);
    } catch {
      return "";
    }
  }
  return "";
}

function extractErrorCode(error: unknown): string | null {
  if (!error) {
    return null;
  }

  let errorParts: string[] = [];

  if (error instanceof Error) {
    if (error.message.length > 0) {
      errorParts.push(error.message);
    }
    if (error.name.length > 0) {
      errorParts.push(error.name);
    }
  } else if (typeof error === "string") {
    errorParts.push(error);
  } else if (typeof error === "object" && error !== null) {
    const errorObj = error as Record<string, unknown>;
    const fromData = convexStyleErrorData(error);
    if (fromData.length > 0) {
      errorParts.push(fromData);
    }
    if (typeof errorObj.message === "string") {
      errorParts.push(errorObj.message);
    }
    if (typeof errorObj.error === "string") {
      errorParts.push(errorObj.error);
    }
    try {
      errorParts.push(JSON.stringify(errorObj));
    } catch {
      errorParts.push(String(error));
    }
  } else {
    errorParts.push(String(error));
  }

  const errorText = errorParts.join("\n");

  let best: { code: string; len: number } | null = null;
  for (const code of Object.keys(AUTH_ERROR_MESSAGES)) {
    if (errorText.includes(code)) {
      if (best === null || code.length > best.len) {
        best = { code, len: code.length };
      }
    }
  }
  if (best !== null) {
    return best.code;
  }

  return null;
}

// ---------------------------------------------------------------------------
// extract user-friendly error message from convex error
// ---------------------------------------------------------------------------

function extractErrorMessage(error: unknown): string {
  if (!error) {
    return "An unknown error occurred";
  }

  let rawMessage = "";
  let errorString = "";

  // handle Error instances
  if (error instanceof Error) {
    rawMessage = error.message || "";
    errorString = error.toString();
  } else if (typeof error === "string") {
    rawMessage = error;
    errorString = error;
  } else if (typeof error === "object" && error !== null) {
    const errorObj = error as Record<string, unknown>;
    if (typeof errorObj.message === "string") {
      rawMessage = errorObj.message;
    } else if (typeof errorObj.error === "string") {
      rawMessage = errorObj.error;
    } else {
      try {
        errorString = JSON.stringify(errorObj);
      } catch {
        errorString = String(error);
      }
    }
  } else {
    errorString = String(error);
  }

  // combine all possible error sources
  const fullErrorText = `${rawMessage}\n${errorString}`.trim();

  // extract the actual error message from Convex error format
  // format: "[CONVEX A(auth:signIn)] [Request ID: ...] Server Error\n\nUncaught Error: Actual message"
  const patterns = [
    // pattern 1: "Uncaught Error: message"
    /Uncaught Error:\s*([^\n]+?)(?:\s+at\s+handler|\s+Called by|\n|$)/s,
    // pattern 2: extract after "Server Error" if present
    /Server Error[\s\n]+(.+?)(?:\s+at\s+handler|\s+Called by|\n|$)/s,
    // pattern 3: look for error message after removing metadata
    /(?:\[CONVEX.*?\]\s*)*\[Request ID:.*?\]\s*Server Error[\s\n]+(.+?)(?:\s+at\s+handler|\s+Called by|\n|$)/s,
  ];

  for (const pattern of patterns) {
    const match = fullErrorText.match(pattern);
    if (match && match[1]) {
      let extracted = match[1].trim();
      // clean up the extracted message
      extracted = extracted
        .replace(/\[CONVEX\s+[AM]\([^)]+\)\]/g, "")
        .replace(/\[Request ID:\s*[^\]]+\]/g, "")
        .replace(/Server Error/gi, "")
        .replace(/Uncaught Error:\s*/gi, "")
        .replace(/\s+/g, " ")
        .trim();

      if (extracted && extracted.length > 0) {
        return extracted;
      }
    }
  }

  // if no pattern matched, clean up the raw message
  let cleaned = rawMessage || errorString;
  cleaned = cleaned
    .replace(/\[CONVEX\s+[AM]\([^)]+\)\]/g, "")
    .replace(/\[Request ID:\s*[^\]]+\]/g, "")
    .replace(/Server Error/gi, "")
    .replace(/Uncaught Error:\s*/gi, "")
    .replace(/\s+/g, " ")
    .trim();

  if (cleaned && cleaned.length > 0 && cleaned !== "{}") {
    return cleaned;
  }

  return "Authentication failed";
}

// ---------------------------------------------------------------------------
// handle auth error and return user-friendly message
// ---------------------------------------------------------------------------

export function handleAuthError(error: unknown): AuthErrorResult {
  const code = extractErrorCode(error);
  const rawMessage = extractErrorMessage(error);

  // if we have a mapped message for this error code, use it
  if (code && AUTH_ERROR_MESSAGES[code]) {
    return {
      message: AUTH_ERROR_MESSAGES[code],
      code,
    };
  }

  // otherwise, use the extracted message or fallback
  return {
    message: rawMessage || "Authentication failed. Please try again.",
    code,
  };
}

// ---------------------------------------------------------------------------
// convex auth client: signIn() return value (not only thrown errors)
// ---------------------------------------------------------------------------

/**
 * Password / credentials sign-in resolves with `{ signingIn: boolean }` even when
 * no session is created. Treat `signingIn: false` (and missing success) as failure
 * so the UI does not spin forever.
 */
export function interpretSignInClientReturn(
  value: unknown,
): { ok: true } | { ok: false; error: AuthErrorResult } {
  if (value === null || value === undefined) {
    return {
      ok: false,
      error: handleAuthError(new Error("SignInIncomplete")),
    };
  }
  if (typeof value !== "object") {
    return {
      ok: false,
      error: handleAuthError(new Error("SignInIncomplete")),
    };
  }
  const o = value as Record<string, unknown>;
  if (o["redirect"] !== undefined) {
    return { ok: true };
  }
  const signingIn = o["signingIn"];
  if (signingIn === true) {
    return { ok: true };
  }
  if (signingIn === false) {
    return {
      ok: false,
      error: handleAuthError(new Error("SignInIncomplete")),
    };
  }
  return {
    ok: false,
    error: handleAuthError(new Error("SignInIncomplete")),
  };
}
